import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { campaignsAPI } from '../../services/api';
import InvitePlayerModal from './InvitePlayerModal';
import './CampaignDetailsModal.css';

function CampaignDetailsModal({ isOpen, onClose, campaignId, onUpdated, onLaunch }) {
  const { user } = useAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  const [campaign, setCampaign] = useState(null);
  const [characters, setCharacters] = useState([]);

  const [form, setForm] = useState({
    name: '',
    description: '',
    startingXp: 1000
  });

  const roleNorm = useMemo(() => (user?.role || '').toString().trim().toUpperCase(), [user?.role]);
  const isAdmin = roleNorm === 'ADMIN';
  const canEdit = useMemo(() => {
    if (!campaign) return false;
    return isAdmin || campaign.gmId === user?.id;
  }, [campaign, isAdmin, user?.id]);

  useEffect(() => {
    if (!isOpen || !campaignId) return;
    let mounted = true;

    const load = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const res = await campaignsAPI.getCampaignDetails(campaignId);
        if (!mounted) return;
        const c = res.data?.campaign;
        const chars = res.data?.characters || [];
        setCampaign(c);
        setCharacters(chars);
        setForm({
          name: c?.name || '',
          description: c?.description || '',
          startingXp: Number.isFinite(Number(c?.startingXp)) ? Number(c.startingXp) : 1000
        });
      } catch (e) {
        if (!mounted) return;
        console.error('Failed to load campaign details:', e);
        const status = e?.response?.status;
        const serverMsg =
          (typeof e?.response?.data === 'string' ? e.response.data : e?.response?.data?.error);
        setError(
          serverMsg || (status ? `Failed to load campaign details (HTTP ${status})` : 'Failed to load campaign details')
        );
      } finally {
        if (!mounted) return;
        setIsLoading(false);
      }
    };

    load();
    return () => { mounted = false; };
  }, [isOpen, campaignId]);

  // Close on ESC
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const save = async () => {
    if (!campaignId) return;
    try {
      setIsSaving(true);
      setError(null);

      const payload = {
        name: form.name,
        description: form.description,
        startingXp: Number(form.startingXp)
      };

      const res = await campaignsAPI.updateCampaign(campaignId, payload);
      const updated = res.data?.campaign;
      if (updated) {
        setCampaign(updated);
        onUpdated?.(updated);
      }
    } catch (e) {
      console.error('Failed to save campaign:', e);
      setError(e?.response?.data?.error || 'Failed to save campaign');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <InvitePlayerModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        campaignId={campaignId}
        campaignName={campaign?.name || 'Campaign'}
      />
      
      <div className="campaign-details-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="campaign-details-modal" role="dialog" aria-modal="true" aria-label="Campaign details">
        <div className="campaign-details-header">
          <div>
            <h3>Campaign Details</h3>
            {campaign?.gmName && (
              <p className="sub">GM: {campaign.gmName}</p>
            )}
          </div>

          <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {isLoading ? (
          <div className="campaign-details-body">
            <p>Loading...</p>
          </div>
        ) : error ? (
          <div className="campaign-details-body">
            <p className="error">{error}</p>
          </div>
        ) : (
          <div className="campaign-details-body">
            <div className="campaign-details-grid">
              <div className="card">
                <h4>Basics</h4>

                <label className="field">
                  <span>Campaign Name</span>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                    disabled={!canEdit || isSaving}
                  />
                </label>

                <label className="field">
                  <span>Description</span>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                    disabled={!canEdit || isSaving}
                    rows={4}
                  />
                </label>

                <label className="field">
                  <span>Starting XP for New Characters</span>
                  <input
                    type="number"
                    min={0}
                    step={50}
                    value={form.startingXp}
                    onChange={(e) => setForm((p) => ({ ...p, startingXp: e.target.value }))}
                    disabled={!canEdit || isSaving}
                  />
                  <span className="hint">Default: 1000</span>
                </label>

                <div className="row-actions">
                  {canEdit ? (
                    <button className="btn-primary" onClick={save} disabled={isSaving}>
                      {isSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                  ) : (
                    <span className="hint">You can view details, but only the GM can edit.</span>
                  )}

                  {(canEdit) && (
                    <>
                      <button
                        className="btn-secondary"
                        onClick={() => setIsInviteModalOpen(true)}
                        disabled={isSaving}
                      >
                        Invite Player
                      </button>
                      <button
                        className="btn-secondary"
                        onClick={() => onLaunch?.(campaign)}
                        disabled={isSaving}
                      >
                        Launch Campaign
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className="card">
                <h4>Joined Characters</h4>
                {characters.length === 0 ? (
                  <p className="hint">No characters have joined this campaign yet.</p>
                ) : (
                  <div className="characters-list">
                    {characters.map((ch) => (
                      <div key={ch.id} className="character-row">
                        <div className="character-main">
                          <div className="character-name">{ch.name}</div>
                          <div className="character-meta">
                            <span>{ch.characterType}</span>
                            <span>{ch.availableXp} XP</span>
                          </div>
                        </div>
                        <div className="character-player">{ch.playerName}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
}

export default CampaignDetailsModal;